import { select } from 'd3-selection';
import { scaleLinear } from 'd3-scale';
import { axisTop } from 'd3-axis';
import { max } from 'd3-array';
import { format } from 'd3-format';
import { easeLinear } from 'd3-ease';
import { interval } from 'd3-timer';
import { interpolateNumber } from 'd3-interpolate';
/* Side-effect import: gives selections their .transition() method. */
import 'd3-transition';

/*
 * Animated ranking of cumulative player points across a season.
 *
 * A direct port of the `barchartrace` directive's d3 code. The drawing is
 * unchanged — only the Angular wrapper is gone, replaced by a plain
 * function that renders into a container and returns a stop handle so the
 * caller can cancel the timer on unmount or replay.
 *
 * d3 previously came from a CDN script tag in the AngularJS shell; the
 * modules are now dependencies so the chart cannot break on someone else's
 * uptime.
 */
export function renderBarChartRace(container, options) {
	const {
		data,
		timelineStart = 1,
		timelineEnd,
		title = '',
		topN = 20,
		tickDuration = 1000,
		delayDuration = 1500,
		timelineLabel = (start, current) => `GW ${current}`,
	} = options;

	const height = 720;
	const width = 960;
	const margin = { top: 80, right: 0, bottom: 5, left: 0 };
	const barPadding = (height - (margin.bottom + margin.top)) / (topN * 5);

	const root = select(container);
	root.selectAll('*').remove();

	const svg = root
		.append('svg')
		.attr('preserveAspectRatio', 'xMinYMin meet')
		.attr('viewBox', `0 0 ${width} ${height}`);

	svg.append('text').attr('class', 'barChartRaceTitle').attr('y', 24).text(title);

	let current = timelineStart;
	let lastValues = {};

	/* Cumulative totals to the current gameweek, top N only. */
	function frame(first = false) {
		const values = {};
		const rows = [];
		for (const [key, entry] of Object.entries(data)) {
			const lastValue = lastValues[key] ?? 0;
			const step = current < entry.data.length ? entry.data[current] : 0;
			const value = step + lastValue;
			rows.push({ name: entry.name, value, lastValue, colour: entry.color });
			values[key] = value;
		}
		if (!first) lastValues = values;
		return rows.sort((a, b) => b.value - a.value).slice(0, topN);
	}

	let slice = frame(true);
	slice.forEach((d, i) => {
		d.rank = i;
	});

	const x = scaleLinear()
		.domain([0, max(slice, (d) => d.value) || 1])
		.range([margin.left, width - margin.right - 65]);

	const y = scaleLinear().domain([topN, 0]).range([height - margin.bottom, margin.top]);

	const xAxis = axisTop()
		.scale(x)
		.ticks(width > 500 ? 5 : 2)
		.tickSize(-(height - margin.top - margin.bottom))
		.tickFormat((d) => format(',')(d));

	svg
		.append('g')
		.attr('class', 'axis xAxis')
		.attr('transform', `translate(0, ${margin.top})`)
		.call(xAxis)
		.selectAll('.tick line')
		.classed('origin', (d) => d === 0);

	svg
		.selectAll('rect.bar')
		.data(slice, (d) => d.name)
		.enter()
		.append('rect')
		.attr('class', 'bar')
		.attr('x', x(0) + 1)
		.attr('width', (d) => x(d.lastValue) - x(0))
		.attr('y', (d) => y(d.rank) + 5)
		.attr('height', y(1) - y(0) - barPadding)
		.style('fill', (d) => d.colour);

	svg
		.selectAll('text.label')
		.data(slice, (d) => d.name)
		.enter()
		.append('text')
		.attr('class', 'label')
		.attr('x', (d) => x(d.lastValue) - 8)
		.attr('y', (d) => y(d.rank) + 5 + (y(1) - y(0)) / 2 + 1)
		.style('text-anchor', 'end')
		.text((d) => d.name);

	svg
		.selectAll('text.valueLabel')
		.data(slice, (d) => d.name)
		.enter()
		.append('text')
		.attr('class', 'valueLabel')
		.attr('x', (d) => x(d.lastValue) + 5)
		.attr('y', (d) => y(d.rank) + 5 + (y(1) - y(0)) / 2 + 1)
		.text((d) => d.lastValue);

	const timelineText = svg
		.append('text')
		.attr('class', 'timelineText')
		.attr('x', width - margin.right)
		.attr('y', height - 25)
		.style('text-anchor', 'end')
		.text(timelineLabel(timelineStart, current));

	const ticker = interval(() => {
		slice = frame();
		slice.forEach((d, i) => {
			d.rank = i;
		});

		x.domain([0, max(slice, (d) => d.value) || 1]);

		svg.select('.xAxis').transition().duration(tickDuration).ease(easeLinear).call(xAxis);

		const bars = svg.selectAll('.bar').data(slice, (d) => d.name);

		bars
			.enter()
			.append('rect')
			.attr('class', 'bar')
			.attr('x', x(0) + 1)
			.attr('width', (d) => x(d.value) - x(0))
			.attr('y', y(topN + 1) + 5)
			.attr('height', y(1) - y(0) - barPadding)
			.style('fill', (d) => d.colour)
			.transition()
			.duration(tickDuration)
			.ease(easeLinear)
			.attr('y', (d) => y(d.rank) + 5);

		bars
			.transition()
			.duration(tickDuration)
			.ease(easeLinear)
			.attr('width', (d) => Math.max(0, x(d.value) - x(0)))
			.attr('y', (d) => y(d.rank) + 5);

		bars
			.exit()
			.transition()
			.duration(tickDuration)
			.ease(easeLinear)
			.attr('width', (d) => Math.max(0, x(d.value) - x(0)))
			.attr('y', y(topN + 1) + 5)
			.remove();

		const labels = svg.selectAll('.label').data(slice, (d) => d.name);

		labels
			.enter()
			.append('text')
			.attr('class', 'label')
			.attr('x', (d) => x(d.value) - 8)
			.attr('y', y(topN + 1) + 5 + (y(1) - y(0)) / 2)
			.style('text-anchor', 'end')
			.text((d) => d.name)
			.transition()
			.duration(tickDuration)
			.ease(easeLinear)
			.attr('y', (d) => y(d.rank) + 5 + (y(1) - y(0)) / 2 + 1);

		labels
			.transition()
			.duration(tickDuration)
			.ease(easeLinear)
			.attr('x', (d) => x(d.value) - 8)
			.attr('y', (d) => y(d.rank) + 5 + (y(1) - y(0)) / 2 + 1);

		labels
			.exit()
			.transition()
			.duration(tickDuration)
			.ease(easeLinear)
			.attr('x', (d) => x(d.value) - 8)
			.attr('y', y(topN + 1) + 5)
			.remove();

		const valueLabels = svg.selectAll('.valueLabel').data(slice, (d) => d.name);

		valueLabels
			.enter()
			.append('text')
			.attr('class', 'valueLabel')
			.attr('x', (d) => x(d.value) + 5)
			.attr('y', y(topN + 1) + 5)
			.text((d) => d.value)
			.transition()
			.duration(tickDuration)
			.ease(easeLinear)
			.attr('y', (d) => y(d.rank) + 5 + (y(1) - y(0)) / 2 + 1);

		valueLabels
			.transition()
			.duration(tickDuration)
			.ease(easeLinear)
			.attr('x', (d) => x(d.value) + 5)
			.attr('y', (d) => y(d.rank) + 5 + (y(1) - y(0)) / 2 + 1)
			.tween('text', function tweenText(d) {
				const i = interpolateNumber(d.lastValue, d.value);
				return function step(t) {
					this.textContent = Math.max(0, Math.trunc(i(t)));
				};
			});

		valueLabels
			.exit()
			.transition()
			.duration(tickDuration)
			.ease(easeLinear)
			.attr('x', (d) => x(d.value) + 5)
			.attr('y', y(topN + 1) + 5)
			.remove();

		timelineText.text(timelineLabel(timelineStart, current));

		current += 1;
		if (current > timelineEnd) ticker.stop();
	}, delayDuration);

	return () => ticker.stop();
}
